"use client";

import {
  defaultMasteryIntervalDays,
  defaultMasteryPlan,
  maximumMasteryIntervalDays,
  methodDefaults,
  methodHelp,
  methodLabel,
  methodNames,
  type PracticeMethod,
  parseMasteryPlan,
  parsePracticeMethod,
} from "@tamariba/drills";
import { type FormEvent, useRef, useState } from "react";
import { useAuthoringScope } from "../authoring-scope";
import { PracticeToolIcon } from "../practice-tool-icon";
import { FieldLabel } from "../ui/field-label";
import { DrillBreadcrumbs } from "./breadcrumbs";
import { DrillIcon } from "./icon";
import { DrillError, useLeaveWarning } from "./shared";
import {
  type DrillCollection,
  type DrillCollectionWrite,
  RequestError,
  request,
} from "./types";

export function DrillCollectionEditor({
  collection,
  onSaved,
  onCancel,
}: {
  collection?: DrillCollection;
  onSaved: (collection: DrillCollection) => void;
  onCancel: () => void;
}) {
  const authoring = useAuthoringScope();
  const isPlaylist = collection?.isPlaylist ?? false;
  const [form, setForm] = useState<DrillCollectionWrite>({
    title: collection?.title ?? "",
    description: collection?.description ?? "",
    revision: collection?.revision ?? 0,
  });
  const [dirty, setDirty] = useState(false);
  const [recommendationEnabled, setRecommendationEnabled] = useState(
    collection
      ? ((collection as DrillCollection & { recommendationEnabled?: boolean })
          .recommendationEnabled ?? true)
      : false,
  );
  const initialMethod = parsePracticeMethod(
    collection?.practiceMethod ?? methodDefaults.consecutive,
  );
  const [methodType, setMethodType] = useState<PracticeMethod["type"]>(
    initialMethod.type,
  );
  const methodNumbers = (method: PracticeMethod): Record<string, string> =>
    Object.fromEntries(
      Object.entries(method)
        .filter(([key]) => key !== "type")
        .map(([key, value]) => [key, String(value)]),
    );
  const [numbers, setNumbers] = useState(() => methodNumbers(initialMethod));
  const initialIntervals =
    collection?.masteryPlan.intervalsDays ?? defaultMasteryPlan.intervalsDays;
  const [masteryTarget, setMasteryTarget] = useState(
    String(initialIntervals.length),
  );
  const [intervals, setIntervals] = useState(initialIntervals.map(String));
  const method = {
    type: methodType,
    ...Object.fromEntries(
      Object.entries(numbers).map(([key, value]) => [key, Number(value)]),
    ),
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkSaved, setCheckSaved] = useState(false);
  let previewMethod: PracticeMethod | undefined;
  try {
    previewMethod = parsePracticeMethod(method);
  } catch {
    /* Incomplete numeric input is validated on save. */
  }
  const recordId = useRef(collection?.id ?? "");
  useLeaveWarning(dirty);
  function cancel() {
    if (
      !busy &&
      (!dirty || window.confirm("保存していない変更を破棄しますか？"))
    )
      onCancel();
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setCheckSaved(false);
    recordId.current ||= crypto.randomUUID();
    try {
      const practiceMethod =
        isPlaylist || !recommendationEnabled
          ? undefined
          : parsePracticeMethod(method);
      if (
        !isPlaylist &&
        recommendationEnabled &&
        (Number(masteryTarget) !== intervals.length ||
          intervals.some((days) => days.trim() === ""))
      )
        throw new Error("達成目標回数と各回の間隔を入力してください。");
      const masteryPlan =
        isPlaylist || !recommendationEnabled
          ? undefined
          : parseMasteryPlan({
              intervalsDays: intervals.map(Number),
            });
      const saved = await request<DrillCollection>(
        authoring.api(`/api/drill-collections/${recordId.current}`),
        {
          ...form,
          practiceMethod,
          masteryPlan,
          ...(!isPlaylist && { recommendationEnabled }),
        },
      );
      setDirty(false);
      onSaved(saved);
    } catch (error) {
      setError((error as Error).message);
      setCheckSaved(
        error instanceof RequestError &&
          (error.status === 0 || error.status === 409),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="drill-editor-page drill-collection-editor-page">
      <DrillBreadcrumbs
        collection={collection}
        current={collection ? "編集" : "ドリル集を作成"}
      />
      <div className="drill-heading drill-editor-heading">
        <div>
          <h1>{collection ? "ドリル集を編集" : "ドリル集を作成"}</h1>
          <p className="drill-description">
            {isPlaylist
              ? "選択したドリル集の名前と説明を編集します。"
              : "テーマと練習の進め方を決めて、配置練習をまとめましょう。"}
          </p>
        </div>
      </div>
      <form
        className="drill-edit-form drill-collection-form drill-designed-form"
        onSubmit={(event) => void save(event)}
      >
        <fieldset className="drill-editor-fields" disabled={busy}>
          <section
            className="drill-panel"
            aria-labelledby="collection-basics-heading"
          >
            <div className="drill-editor-section-heading">
              <span aria-hidden="true">01</span>
              <div>
                <h2 id="collection-basics-heading">基本情報</h2>
                <p>どんな練習を集めるドリル集ですか？</p>
              </div>
            </div>
            <label className="drill-field">
              <FieldLabel required>ドリル集の名前</FieldLabel>
              <input
                required
                maxLength={120}
                value={form.title}
                onChange={(event) => {
                  setDirty(true);
                  setForm({ ...form, title: event.target.value });
                }}
                placeholder="例：基本ショット、ポジショニング"
              />
            </label>
            <label className="drill-field">
              <FieldLabel required={false}>説明</FieldLabel>
              <textarea
                rows={3}
                maxLength={1000}
                value={form.description}
                onChange={(event) => {
                  setDirty(true);
                  setForm({ ...form, description: event.target.value });
                }}
                placeholder="どんな配置を集めるか、何を練習したいか。"
              />
            </label>
            {!collection && (
              <p className="drill-collection-form-hint">
                <PracticeToolIcon name="drills" size={18} />{" "}
                作成したドリル集の中に、個別のドリルを追加できます。
              </p>
            )}
          </section>
          {!isPlaylist && (
            <>
              <section className="drill-panel">
                <h2>おすすめの練習方法</h2>
                <p>
                  作成者のおすすめを提案できます。練習する人は自分で変更できます。
                </p>
                <label>
                  <input
                    type="checkbox"
                    checked={recommendationEnabled}
                    onChange={(event) => {
                      setRecommendationEnabled(event.target.checked);
                      setDirty(true);
                    }}
                  />{" "}
                  おすすめを設定する
                </label>
                {!recommendationEnabled && (
                  <p>
                    設定がない場合は、プレイヤーのクラスに応じた方法を提案します。
                  </p>
                )}
              </section>
              {recommendationEnabled && (
                <>
                  <section
                    className="drill-panel drill-method-settings"
                    aria-labelledby="practice-method-heading"
                  >
                    <div className="drill-editor-section-heading">
                      <span aria-hidden="true">02</span>
                      <div>
                        <h2 id="practice-method-heading">1回の練習方法</h2>
                        <p>1回の練習を、どの条件で終えるか決めます。</p>
                      </div>
                    </div>
                    <div className="drill-editor-method-inputs">
                      <label className="drill-field">
                        <FieldLabel required>練習方法</FieldLabel>
                        <select
                          value={methodType}
                          onChange={(event) => {
                            const type = event.target
                              .value as PracticeMethod["type"];
                            setMethodType(type);
                            setNumbers(methodNumbers(methodDefaults[type]));
                            setDirty(true);
                          }}
                        >
                          {Object.entries(methodNames).map(([type, name]) => (
                            <option key={type} value={type}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="drill-method-numbers">
                        {Object.keys(numbers).map((key) => (
                          <label
                            key={`${methodType}-${key}`}
                            className="drill-field"
                          >
                            <FieldLabel required>
                              {key === "minutes"
                                ? "練習時間（分）"
                                : key === "attempts"
                                  ? "挑戦回数（回）"
                                  : methodType === "consecutive"
                                    ? "連続成功回数（回）"
                                    : "成功回数（回）"}
                            </FieldLabel>
                            <input
                              type="number"
                              inputMode="numeric"
                              required
                              min={1}
                              max={key === "minutes" ? 300 : 1000}
                              step={1}
                              value={numbers[key] ?? ""}
                              onChange={(event) => {
                                setNumbers({
                                  ...numbers,
                                  [key]: event.target.value,
                                });
                                setDirty(true);
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                    <p className="drill-method-rule">
                      {methodHelp[methodType]}
                    </p>
                    {previewMethod && (
                      <p className="drill-method-summary" role="status">
                        <DrillIcon name="check" size={18} />
                        {methodLabel(previewMethod)}
                      </p>
                    )}
                    <small>
                      おすすめは集内の全ドリルに提案します。回数は1〜1000回、時間は1〜300分です。
                    </small>
                  </section>
                  <section
                    className="drill-panel drill-mastery-settings"
                    aria-labelledby="mastery-plan-heading"
                  >
                    <div className="drill-editor-section-heading">
                      <span aria-hidden="true">03</span>
                      <div>
                        <h2 id="mastery-plan-heading">マスターまでの目標</h2>
                        <p>
                          各ドリルを繰り返し達成し、技能を定着させましょう。
                        </p>
                      </div>
                    </div>
                    <div className="drill-editor-mastery-grid">
                      <div className="drill-editor-mastery-target">
                        <label className="drill-field drill-mastery-target">
                          <FieldLabel required>達成目標回数（回）</FieldLabel>
                          <input
                            type="number"
                            inputMode="numeric"
                            required
                            min={1}
                            max={1000}
                            step={1}
                            value={masteryTarget}
                            onChange={(event) => {
                              const value = event.target.value;
                              setMasteryTarget(value);
                              setDirty(true);
                              const target = Number(value);
                              if (
                                Number.isInteger(target) &&
                                target >= 1 &&
                                target <= 1000
                              )
                                setIntervals((current) =>
                                  Array.from(
                                    { length: target },
                                    (_, index) =>
                                      current[index] ??
                                      String(
                                        defaultMasteryIntervalDays(index) ?? "",
                                      ),
                                  ),
                                );
                            }}
                          />
                        </label>
                        <p>
                          上で設定した練習を達成・完了して保存すると1回。ドリルごとに数えます。
                        </p>
                      </div>
                      <div className="drill-editor-mastery-schedule">
                        <div className="drill-mastery-interval-heading">
                          <strong>
                            <FieldLabel required>取り組む間隔</FieldLabel>
                          </strong>
                          <span>前回の達成日から</span>
                        </div>
                        <ol className="drill-mastery-intervals">
                          {intervals.map((days, index) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: milestone identity is its fixed ordinal.
                            <li key={index}>
                              <span className="drill-milestone-number">
                                {index + 1}
                              </span>
                              <span>{index + 1}回目</span>
                              {index === 0 ? (
                                <strong>当日</strong>
                              ) : (
                                <label>
                                  <span className="drill-sr-only">
                                    {index + 1}回目の間隔（日）
                                  </span>
                                  <input
                                    type="number"
                                    inputMode="numeric"
                                    required
                                    min={0}
                                    max={maximumMasteryIntervalDays}
                                    step={1}
                                    value={days}
                                    onChange={(event) => {
                                      setIntervals((current) =>
                                        current.map((value, position) =>
                                          position === index
                                            ? event.target.value
                                            : value,
                                        ),
                                      );
                                      setDirty(true);
                                    }}
                                  />
                                  <span>日後</span>
                                </label>
                              )}
                            </li>
                          ))}
                        </ol>
                        <small>
                          初期値は1・2・4・8・16・32…日後。各回で変更できます。
                        </small>
                      </div>
                    </div>
                    {intervals.length > 17 && (
                      <small>
                        18回目以降は初期値が間隔の上限を超えるため、日数を個別に入力してください。
                      </small>
                    )}
                    <small>
                      間隔は目安です。早めの達成も数え、実際の達成日から次の目安を計算します。途中終了は数えません。
                    </small>
                    {collection && (
                      <small>
                        目標や間隔を変更しても、各ドリルの達成回数は引き継ぎます。
                      </small>
                    )}
                  </section>
                </>
              )}
            </>
          )}
        </fieldset>
        {error && (
          <>
            <DrillError message={error} />
            {checkSaved && (
              <a
                href={authoring.href(`/drills/collections/${recordId.current}`)}
                target="_blank"
                rel="noreferrer"
                className="text-link"
              >
                保存先を別のタブで確認 <DrillIcon name="external" size={14} />
              </a>
            )}
          </>
        )}
        <div className="drill-form-actions drill-editor-savebar">
          <button
            type="button"
            className="secondary-button"
            onClick={cancel}
            disabled={busy}
          >
            キャンセル
          </button>
          <button type="submit" className="primary-button" disabled={busy}>
            {busy ? "保存中…" : collection ? "変更を保存" : "ドリル集を作成"}
            <DrillIcon name="check" size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}
